import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';

import DatePicker from 'antd/es/date-picker';

import { datePickerFormats, dateTimeFormat } from 'config/common';
import { StatType } from 'services/stats';

import useQueryParam from 'hooks/useQueryParam';
import useDatePicker from 'hooks/useDatePicker';
import useStats from 'hooks/useStats';

import MainLayout from 'layouts/Main.layout';

import { Row, Col, Space } from 'components/layout';
import { Text } from 'components/Text';
import Grid from 'components/Grid';
import PageHeader from 'components/MainPageHeader';
import BackButton from 'components/BackButton';
import StatsCard from 'components/StatsCard';
import Table from 'components/Table';
import FilterBar from 'components/FilterBar';

import { formatDate, formatDuration, localToUtcTs } from 'helpers/date';
import PeriodFilter from 'components/PeriodFilter';

const CartDetailsPageHeader = styled(PageHeader)`
  padding-bottom: 8px;
  padding-top: 24px;

  & .ant-page-header-heading {
    min-height: 60px;
    max-height: 60px;
    margin-bottom: 8px;
  }

  & .ant-page-header-heading-sub-title {
    display: inline-flex;
    justify-content: center;
    flex: 1;
    margin-right: 110px;
  }
`;

const CartDetailsPage = () => {
  const params = useParams();
  const [period, setPeriod] = useQueryParam('period', 'daily');
  const datePicker = useDatePicker(period, { queryParam: 'dt' });
  
  const history = useHistory();

  const statsParams = useMemo(() => {
    const mode = datePicker.mode;
    const date = datePicker.date;
    if (!date) {
      return {};
    }

    const fromTs = localToUtcTs(date.clone().startOf(mode === 'date' ? 'day' : mode).toDate(), true);
    const toTs = localToUtcTs(date.clone().endOf(mode === 'date' ? 'day' : mode).toDate(), true);
    
    return {
      device_id: parseInt(params.id, 10),
      from: fromTs,
      to: toTs,
    };
  }, [datePicker.date, datePicker.mode, params.id]);

  const { state: { stats } } = useStats(StatType.cartStats, statsParams, {
    autoFetch: true,
    reset: true,
  });

  const { state: { isLoading, stats: cartDetails } } = useStats(StatType.cartDetails, statsParams, {
    autoFetch: true,
    reset: true,
  });

  const summary = useMemo(() => {
    const { last_seen: summaryStats } = stats;
    return summaryStats ? summaryStats[0] || {} : {};
  }, [stats]);

  const details = useMemo(() => {
    return cartDetails && cartDetails.cart ? cartDetails.cart[0] : null;
  }, [cartDetails]);

  const activity = useMemo(() => {
    return cartDetails ? cartDetails.data || [] : [];
  }, [cartDetails]);

  return (
    <MainLayout>
      <CartDetailsPageHeader
        title={
          <Space size={16}>
            {details ? (
              <CartNameWrapper>
                {details.name}
                <Text style={{ fontSize: '12px', lineHeight: 1.5, color: '#A4B0BA', fontWeight: 400, marginLeft: '2px' }}>
                  Cart code
                </Text>
              </CartNameWrapper>
            ) : `Cart ${params.id}`}
          </Space>
        }
        subTitle={
          <PeriodFilter
            defaultActiveKey={period}
            onChange={setPeriod}
          />
        }
        backIcon={
          <BackButton />
        }
        onBack={() => {
          history.goBack();
        }}
      />
      <Row style={{ padding: '12px 24px' }}>
        <Space size="large">
          <DatePicker
            mode={datePicker.mode}
            picker={datePicker.mode}
            value={datePicker.date}
            format={datePickerFormats[period]}
            onChange={date => datePicker.setDate(date)}
            style={{ width: '260px' }}
            allowClear={false}
          />
        </Space>
      </Row>
      <Grid gutter={24} columns={4} style={{ padding: '8px 24px 16px' }}>
        <StatsCard
          title="Avg. time in container"
          value={typeof summary.avg_container_mins === 'number' ? formatDuration(summary.avg_container_mins * 60 * 1000, false) : '-'}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Avg. time in trailer"
          value={typeof summary.avg_trailer_mins === 'number' ? formatDuration(summary.avg_trailer_mins * 60 * 1000, false) : '-'}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Avg. time in transit"
          value={typeof summary.avg_area_mins === 'number' ? formatDuration(summary.avg_area_mins * 60 * 1000, false) : '-'}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Avg. time empty"
          value={typeof summary.avg_empty_mins === 'number' ? formatDuration(summary.avg_empty_mins * 60 * 1000, false) : '-'}
          titleAlign={['center', 'bottom']}
        />
      </Grid>
      <Row style={{ padding: '12px 24px', width: '100%' }}>
        <FilterBar
          title="Activity"
        />
      </Row>
      <Row style={{ padding: '12px 24px' }}>
        <Col span={24}>
          <Table pagination={{ showSizeChanger: false }}
            dataSource={activity}
            loading={isLoading}
            rowKey={'id'}
            columns={[
              {
                type: 'text',
                title: 'CART LOADING',
                align: 'center',
                children: [
                  {
                    type: 'link',
                    title: 'Door',
                    align: 'center',
                    width: '10%',
                    dataIndex: 'container_door_name',
                    to: ({ item }) => `/doors/${item.container_door_id}`
                  },
                  {
                    type: 'link',
                    title: 'Container',
                    align: 'center',
                    width: '15%',
                    dataIndex: 'container_code',
                    to: ({ item }) => `/containers/${item.container_id}`,
                  },
                  {
                    type: 'text',
                    title: 'First Seen at',
                    align: 'center',
                    width: '15%',
                    render: ({ container_enter_ts: enter_ts }) => {
                      if (!enter_ts) return '-';
                      return formatDate(enter_ts * 1000, dateTimeFormat);
                    }
                  },
                  {
                    type: 'text',
                    title: 'Duration',
                    align: 'center',
                    width: '10%',
                    render: ({ container_enter_ts: enter_ts, container_exit_ts: exit_ts }) => {
                      if (!enter_ts) return '-';

                      return formatDuration((exit_ts - enter_ts) * 1000, false);
                    }
                  }
                ]
              },
              {
                type: 'text',
                title: 'CART UNLOADING',
                align: 'center',
                children: [
                  {
                    type: 'link',
                    title: 'Door',
                    width: '10%',
                    align: 'center',
                    dataIndex: 'trailer_door_name',
                    to: ({ item }) => `/doors/${item.trailer_door_id}`
                  },
                  {
                    type: 'link',
                    title: 'Trailer',
                    align: 'center',
                    width: '15%',
                    dataIndex: 'trailer_code',
                    to: ({ item }) => `/trailers/${item.container_id}`,
                  },
                  {
                    type: 'text',
                    title: 'First Seen at',
                    width: '15%',
                    align: 'center',
                    render: ({ trailer_enter_ts: enter_ts }) => {
                      if (!enter_ts) return '-';
                      return formatDate(enter_ts * 1000, dateTimeFormat);
                    }
                  },
                  {
                    type: 'text',
                    title: 'Duration',
                    align: 'center',
                    width: '10%',
                    render: ({ trailer_enter_ts: enter_ts, trailer_leave_ts: exit_ts }) => {
                      if (!enter_ts) return '-';

                      return formatDuration((exit_ts - enter_ts) * 1000, false);
                    }
                  }
                ]
              }
            ]}
          />
        </Col>
      </Row>
    </MainLayout>
  );
};

const CartNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

export default CartDetailsPage;